/**
 * Inbox — the artist's Arthaus Email (handle@arthaus.email). Replaces the Messages screen when the
 * `easel.arthaus_email` flag is on: website enquiries are delivered into this inbox too, so it is
 * the one place every message lands.
 *
 * No inbox yet → the claim card (prefilled from ?claim=<name> when arriving from arthaus.email).
 */

import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { Copy, Inbox as InboxIcon, PenSquare, RefreshCw, Search, Settings2, X } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { useCommissionsAccess } from '@/features/easel/hooks'
import { ClaimInboxCard } from '../components/ClaimInboxCard'
import { ComposeDialog, type ComposeMode } from '../components/ComposeDialog'
import { ThreadList } from '../components/ThreadList'
import { ThreadView } from '../components/ThreadView'
import { useInboxSettings, useInboxState, useSyncInbox, useThreads } from '../hooks/useInbox'
import type { InboxView } from '../types'

const VIEWS: { key: InboxView; label: string; empty: string }[] = [
  { key: 'leads', label: 'Leads', empty: 'No commission leads yet. When someone asks about custom work, it lands here.' },
  { key: 'inbox', label: 'Inbox', empty: 'Nothing here yet. Share your address and mail will appear in seconds.' },
  { key: 'sent', label: 'Sent', empty: 'Nothing sent yet.' },
  { key: 'archive', label: 'Archive', empty: 'Nothing archived.' },
  { key: 'spam', label: 'Spam', empty: 'No spam. Nice.' },
  { key: 'trash', label: 'Trash', empty: 'Trash is empty. Conversations here are deleted after 30 days.' },
]

function SignatureDialog({ open, onClose, value }: { open: boolean; onClose: () => void; value: string }) {
  const settings = useInboxSettings()
  const [text, setText] = useState(value)
  useEffect(() => {
    if (open) setText(value)
  }, [open, value])
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Email signature</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Added to the end of every email you send from this inbox. Leave it empty for none.
        </p>
        <Textarea rows={5} value={text} onChange={(e) => setText(e.target.value)} placeholder={'Shelly Bremmer\nshellybremmer.com · @shellybremmer'} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => settings.mutate({ signature: text }, { onSuccess: onClose })}
            disabled={settings.isPending}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function InboxPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { toast } = useToast()
  const { data: state, isLoading } = useInboxState()
  const [view, setView] = useState<InboxView>('leads')
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [compose, setCompose] = useState<ComposeMode | null>(null)
  const [signatureOpen, setSignatureOpen] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const hasInbox = Boolean(state?.inbox)
  const threads = useThreads(view, hasInbox, search)

  // Search as you type, without a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300)
    return () => clearTimeout(t)
  }, [searchInput])
  const sync = useSyncInbox()
  const settings = useInboxSettings()
  const { data: commissionsAccess } = useCommissionsAccess()

  if (isLoading || !state) {
    return (
      <div className="space-y-4 p-8">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[480px] w-full" />
      </div>
    )
  }

  if (!state.inbox) {
    return (
      <div className="p-8">
        <ClaimInboxCard
          domain={state.domain}
          configured={state.configured}
          suggestion={state.suggestion}
          initialName={params.get('claim')}
        />
      </div>
    )
  }

  const inbox = state.inbox
  const counts = state.counts
  const countFor = (key: InboxView) =>
    key === 'leads' ? counts?.openLeads : key === 'inbox' ? counts?.unread : undefined

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-4 md:px-8 md:py-5">
        <div className="flex items-center gap-3">
          <InboxIcon className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Inbox</h1>
            <button
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
              onClick={() => {
                navigator.clipboard.writeText(inbox.emailAddress)
                toast({ title: 'Address copied', description: 'Put it in your Instagram bio and on your site.' })
              }}
            >
              {inbox.emailAddress} <Copy className="h-3 w-3" />
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {counts && counts.converted > 0 && (
            <Badge variant="outline">{counts.converted} became commissions</Badge>
          )}
          <Button size="sm" onClick={() => setCompose({ kind: 'new' })}>
            <PenSquare className="mr-2 h-3.5 w-3.5" /> Compose
          </Button>
          <Button variant="outline" size="sm" onClick={() => sync.mutate()} disabled={sync.isPending} title="Check mail">
            <RefreshCw className={cn('h-3.5 w-3.5 sm:mr-2', sync.isPending && 'animate-spin')} />
            <span className="hidden sm:inline">Check mail</span>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <Settings2 className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72 space-y-3 p-3">
              <DropdownMenuLabel className="px-0">Studio manager</DropdownMenuLabel>
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="auto-draft" className="text-sm font-normal">Draft replies for new mail</Label>
                <Switch
                  id="auto-draft"
                  checked={inbox.autoDraft}
                  onCheckedChange={(v) => settings.mutate({ autoDraft: v })}
                />
              </div>
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="notify" className="text-sm font-normal">Email me about new conversations</Label>
                <Switch
                  id="notify"
                  checked={inbox.notifyPersonal}
                  onCheckedChange={(v) => settings.mutate({ notifyPersonal: v })}
                />
              </div>
              <DropdownMenuSeparator />
              <button
                className="block text-sm text-muted-foreground hover:text-foreground"
                onClick={() => setSignatureOpen(true)}
              >
                {inbox.signature ? 'Edit signature' : 'Add a signature'} →
              </button>
              <button
                className="block text-sm text-muted-foreground hover:text-foreground"
                onClick={() => navigate('/app/easel/contact')}
              >
                Website form history →
              </button>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[380px_1fr]">
        {/* On a phone the list and the conversation are separate screens. */}
        <div className={cn('min-h-0 flex-col border-r', selectedId ? 'hidden md:flex' : 'flex')}>
          <div className="relative border-b px-3 py-2">
            <Search className="absolute left-5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search mail"
              className="h-8 pl-8 pr-8"
            />
            {searchInput && (
              <button
                aria-label="Clear search"
                className="absolute right-5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => setSearchInput('')}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <div className={cn('flex gap-1 overflow-x-auto border-b px-2 py-2', search && 'hidden')}>
            {VIEWS.map((v) => (
              <Button
                key={v.key}
                variant={view === v.key ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => {
                  setView(v.key)
                  setSelectedId(null)
                }}
              >
                {v.label}
                {countFor(v.key) ? (
                  <Badge variant={v.key === 'leads' ? 'default' : 'secondary'} className="ml-1.5 h-5 px-1.5">
                    {countFor(v.key)}
                  </Badge>
                ) : null}
              </Button>
            ))}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            <ThreadList
              threads={threads.data?.data}
              isLoading={threads.isLoading}
              selectedId={selectedId}
              onSelect={setSelectedId}
              showRecipient={view === 'sent'}
              emptyText={search ? `Nothing matches "${search}".` : VIEWS.find((v) => v.key === view)?.empty || ''}
            />
          </div>
        </div>
        <div className={cn('min-h-0', !selectedId && 'hidden md:block')}>
          {selectedId ? (
            <ThreadView
              threadId={selectedId}
              inboxAddress={inbox.emailAddress}
              hasSignature={Boolean(inbox.signature?.trim())}
              commissionsHref={(id) =>
                commissionsAccess?.enabled ? `/app/easel/commissions/${id}` : '/app/easel/inbox'
              }
              onForward={(threadId, subject) => setCompose({ kind: 'forward', threadId, subject })}
              onBack={() => setSelectedId(null)}
            />
          ) : (
            <div className="flex h-full items-center justify-center p-8 text-center text-sm text-muted-foreground">
              Pick a conversation. Leads come first — they are the ones worth money.
            </div>
          )}
        </div>
      </div>
      <ComposeDialog
        mode={compose}
        onClose={() => setCompose(null)}
        signature={inbox.signature || ''}
        fromAddress={inbox.fromName ? `${inbox.fromName} <${inbox.emailAddress}>` : inbox.emailAddress}
      />
      <SignatureDialog open={signatureOpen} onClose={() => setSignatureOpen(false)} value={inbox.signature || ''} />
    </div>
  )
}
